import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useInView } from '../../hooks/useInView';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goglobalworld.net';
  
  useSEO({
    title: 'Direct Import Construction Services | Wholesale Building Materials Sourcing | Save 20-40%',
    description: 'Comprehensive direct import services for wholesale construction materials. Factory-direct sourcing, customs clearance, logistics coordination, and jobsite delivery. Bulk construction materials supplier for developers and contractors. Steel building packages, kitchens, windows, flooring. Eliminate distributor markup and reduce construction costs by 20-40% with transparent pricing.',
    keywords: 'wholesale construction materials, direct import building materials, bulk construction materials supplier, factory direct sourcing, construction logistics services, steel building supplier, save on building materials',
    canonical: `${siteUrl}/services`,
    ogImage: 'https://public.readdy.ai/ai/img_res/e4fccf1ecdc0d4dbc9586177163c50d1.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Direct Import Construction Materials Services',
      description: 'Factory-direct wholesale building materials sourcing and delivery services for custom home builders, developers, and contractors',
      provider: {
        '@type': 'Organization',
        name: 'Go Global Inc.',
      },
      areaServed: {
        '@type': 'Place',
        name: 'Canada',
      },
      serviceType: 'Wholesale Construction Materials Supply',
      offers: {
        '@type': 'Offer',
        description: 'Save 20-40% on construction costs through direct import building materials'
      }
    },
  });

  const [heroRef, heroInView] = useInView({ threshold: 0.2 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.2 });

  const services = [
    {
      title: 'Global Sourcing',
      description: 'Access to 5,000+ verified manufacturing partners across 40+ countries. We identify, vet, and connect you with the right suppliers for your specific project requirements.',
      capabilities: [
        'Factory verification and quality audits',
        'Competitive bid management',
        'Product specification matching',
        'Sample coordination and testing',
        'Supplier relationship management',
      ],
      image: 'https://readdy.ai/api/search-image?query=modern%20manufacturing%20facility%20interior%20with%20organized%20production%20lines%20and%20quality%20control%20processes%20in%20clean%20industrial%20setting%20with%20professional%20workers&width=800&height=600&seq=service-sourcing-001&orientation=landscape',
      icon: 'ri-global-line',
    },
    {
      title: 'Wholesale Procurement',
      description: 'Direct factory pricing eliminates middlemen and reduces costs by 20-40%. Our purchasing power and established relationships ensure the best possible terms.',
      capabilities: [
        'Volume discount negotiation',
        'Payment term optimization',
        'Currency risk management',
        'Purchase order management',
        'Quality assurance protocols',
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20negotiation%20in%20modern%20office%20with%20contracts%20and%20documents%20showing%20wholesale%20procurement%20and%20partnership%20agreements%20in%20bright%20corporate%20setting&width=800&height=600&seq=service-procurement-001&orientation=landscape',
      icon: 'ri-shopping-cart-line',
    },
    {
      title: 'Logistics Coordination',
      description: 'End-to-end supply chain management from factory pickup to jobsite delivery. We handle all transportation, tracking, and coordination across multiple carriers and modes.',
      capabilities: [
        'Multi-modal transportation planning',
        'Real-time shipment tracking',
        'Warehouse and distribution management',
        'Last-mile delivery coordination',
        'Risk mitigation and insurance',
      ],
      image: 'https://readdy.ai/api/search-image?query=modern%20logistics%20control%20center%20with%20digital%20tracking%20screens%20showing%20global%20shipping%20routes%20and%20cargo%20management%20in%20professional%20operations%20room&width=800&height=600&seq=service-logistics-001&orientation=landscape',
      icon: 'ri-truck-line',
    },
    {
      title: 'Customs Expertise',
      description: 'In-house customs clearance in Canada and the United States. Our licensed brokers ensure compliant, efficient processing with minimal delays.',
      capabilities: [
        'Customs clearance (Canada & US)',
        'Tariff classification and optimization',
        'Duty drawback programs',
        'Compliance documentation',
        'Regulatory advisory services',
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20customs%20clearance%20office%20with%20organized%20documentation%20and%20computer%20systems%20showing%20international%20trade%20compliance%20in%20modern%20clean%20setting&width=800&height=600&seq=service-customs-001&orientation=landscape',
      icon: 'ri-file-shield-line',
    },
    {
      title: 'Documentation Support',
      description: 'Comprehensive international shipping documentation for deliveries worldwide. We prepare all required paperwork to ensure smooth customs processing in any country.',
      capabilities: [
        'Commercial invoice preparation',
        'Certificate of origin processing',
        'Packing list generation',
        'Bill of lading management',
        'Country-specific documentation',
      ],
      image: 'https://readdy.ai/api/search-image?query=organized%20office%20desk%20with%20international%20shipping%20documents%20and%20digital%20systems%20showing%20professional%20documentation%20management%20in%20bright%20modern%20workspace&width=800&height=600&seq=service-documentation-001&orientation=landscape',
      icon: 'ri-file-list-line',
    },
    {
      title: 'Construction Management & Installation',
      description: 'Professional on-site construction management and installation services to ensure your materials are properly installed and integrated. Our experienced teams coordinate with contractors and manage installation timelines.',
      capabilities: [
        'On-site installation coordination',
        'Contractor liaison and supervision',
        'Quality control and inspection',
        'Installation timeline management',
        'Post-installation support and warranty',
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20construction%20site%20with%20workers%20installing%20building%20materials%20and%20project%20manager%20overseeing%20installation%20work%20in%20organized%20active%20construction%20environment%20with%20safety%20equipment%20and%20modern%20tools&width=800&height=600&seq=service-construction-mgmt-001&orientation=landscape',
      icon: 'ri-tools-line',
    },
    {
      title: 'Project Management',
      description: 'Dedicated account managers integrate with your project team to ensure materials arrive on schedule and within budget. We become an extension of your operations.',
      capabilities: [
        'Dedicated account management',
        'Schedule coordination and tracking',
        'Budget monitoring and reporting',
        'Stakeholder communication',
        'Issue resolution and escalation',
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20project%20management%20team%20meeting%20in%20modern%20office%20with%20construction%20plans%20and%20digital%20dashboards%20showing%20coordination%20and%20planning&width=800&height=600&seq=service-project-001&orientation=landscape',
      icon: 'ri-team-line',
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Consultation',
      description: 'We discuss your project requirements, timeline, and budget to understand your needs.',
    },
    {
      number: '02',
      title: 'Sourcing',
      description: 'Our team identifies optimal suppliers and negotiates competitive pricing on your behalf.',
    },
    {
      number: '03',
      title: 'Procurement',
      description: 'We manage purchase orders, quality control, and factory coordination.',
    },
    {
      number: '04',
      title: 'Logistics',
      description: 'Complete transportation management from factory to your jobsite.',
    },
    {
      number: '05',
      title: 'Customs',
      description: 'We handle all clearance and documentation for seamless border crossing.',
    },
    {
      number: '06',
      title: 'Delivery',
      description: 'Final delivery coordination ensures materials arrive when and where you need them.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="text-xs font-semibold text-primary-600 tracking-widest uppercase mb-6">
                OUR SERVICES
              </div>
              <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl text-gray-900 leading-tight mb-8">
                Comprehensive <br />
                <span className="font-semibold">Supply Chain Solutions</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                From global sourcing to final delivery, we manage every aspect of your construction 
                material supply chain with precision and expertise.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section ref={servicesRef} className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="space-y-16">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-xl">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-96 object-cover object-center"
                      />
                      <div className="absolute top-6 left-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl shadow-lg">
                          <i className={`${service.icon} text-primary-600 text-3xl`}></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h2 className="font-display font-semibold text-3xl md:text-4xl text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm text-gray-900 uppercase tracking-wide">
                        Key Capabilities
                      </h3>
                      <ul className="space-y-2">
                        {service.capabilities.map((capability, capIndex) => (
                          <li key={capIndex} className="flex items-start">
                            <i className="ri-check-line text-primary-600 text-lg mr-2 mt-0.5"></i>
                            <span className="text-gray-700">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center mt-6 text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      Learn more
                      <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section ref={processRef} className="py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={processInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
                Our <span className="font-semibold">Process</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                A streamlined six-step approach to delivering your materials on time and on budget
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={processInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="relative"
                >
                  <div className="bg-white rounded-xl p-8 border border-gray-100 hover:shadow-lg transition-shadow h-full">
                    <div className="text-5xl font-display font-bold text-primary-100 mb-4">
                      {step.number}
                    </div>
                    <h3 className="font-display font-semibold text-xl text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-2xl text-primary-300"></i>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-primary-600 to-secondary-600">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="font-display font-light text-4xl md:text-5xl text-white mb-6">
              Ready to <span className="font-semibold">Get Started?</span>
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Let's discuss how we can optimize your construction supply chain
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-10 py-4 bg-white text-gray-900 text-base font-semibold rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Request Pricing
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;